<script>
import {computed, onMounted, ref} from "vue";
import useValidation from "@/use/Validation";
import {useAdditionalFieldStore} from "@/stores/AdditionalField";
import useSharedData from "@/use/SharedData";
import useTools from "@/use/Tools";
import useSharedMethod from "@/use/SharedMethod";
import axios from "axios";

export default {
    props: {
        addAction: Boolean,
        updateAction: Boolean,
        deleteAction: Boolean,
        viewAction: Boolean,
    },
    setup(props) {
        const {mxRules} = useValidation();

        const readonly = computed(() => props.deleteAction || props.viewAction);

        const {loading, overlay, dialog} = useSharedData();

        const {$route, $refs, $i18n} = useTools();

        const {getBack} = useSharedMethod();

        const additionalFieldStore = useAdditionalFieldStore();

        const fields = [
            {id: "TEXT_FIELD", name: $i18n.t('additional_field.field.TEXT_FIELD')},
            {id: "SELECT", name: $i18n.t('additional_field.field.SELECT')},
            {id: "DATE_PICKER", name: $i18n.t('additional_field.field.DATE_PICKER')},
            {id: "MONTH_PICKER", name: $i18n.t('additional_field.field.MONTH_PICKER')},
        ];


        const additionalField = ref({
            required: false,
            data: []
        });


        function findById(id) {
            loading.value = true;
            axios.get(`/additionalFields/${id}`).then((response) => {
                additionalField.value = response.data;
            }).finally(() => {
                loading.value = false;
            });
        }

        function save() {
            if ($refs.form.validate()) {
                loading.value = true;
                axios.post('/additionalFields', additionalField.value).then((response) => {
                    additionalFieldStore.addAdditionalField(response.data);
                    getBack();
                }).finally(() => {
                    loading.value = false;
                });
            }
        }

        function update() {
            if ($refs.form.validate()) {
                loading.value = true;
                axios.put('/additionalFields', additionalField.value).then((response) => {
                    additionalFieldStore.updateAdditionalField(response.data);
                    getBack();
                }).finally(() => {
                    loading.value = false;
                });
            }
        }

        function remove() {
            loading.value = true;
            axios.delete(`/additionalFields/${additionalField.value.id}`).then(() => {
                additionalFieldStore.deleteAdditionalField(additionalField.value);
                getBack();
            }).finally(() => {
                loading.value = false;
            });
        }

        onMounted(() => {
            if (!props.addAction) {
                findById($route.params.id);
            }
        })

        return {
            mxRules,
            readonly,
            additionalFieldStore,
            loading,
            overlay,
            dialog,
            additionalField,
            fields,
            save,
            update,
            remove,
            getBack,
        }
    },
};
</script>

<template>
    <v-dialog
        transition="dialog-bottom-transition"
        v-model="dialog"
        persistent
        max-width="800"
        scrollable
    >
        <v-card flat>
            <w-dialog-toolbar
                :add-action="addAction"
                :update-action="updateAction"
                :delete-action="deleteAction"
                :view-action="viewAction"
            ></w-dialog-toolbar>
            <v-card-text>
                <w-overlay
                    :loading="loading"
                    :overlay="overlay"
                    :delete-action="deleteAction"
                    @cancel="overlay = false"
                    @remove="remove"
                ></w-overlay>
                <v-card-text>
                    <v-form ref="form" lazy-validation>
                        <v-container fluid>
                            <w-fieldset :title="$t('common.details')">
                                <v-row dense>
                                    <v-col cols="6">
                                        <w-label :label="$t('common.code')" required/>
                                        <w-text-field
                                            v-model="additionalField.code"
                                            :readonly="readonly"
                                            :rules="[mxRules.required]"
                                        />
                                    </v-col>
                                    <v-col cols="6">
                                        <w-label :label="$t('common.label')" required/>
                                        <w-text-field
                                            v-model="additionalField.label"
                                            :readonly="readonly"
                                            :rules="[mxRules.required]"
                                        />
                                    </v-col>
                                    <v-col cols="6">
                                        <w-label :label="$t('common.module')" required/>
                                        <w-enum-select
                                            v-model="additionalField.module"
                                            :items="[
                                            { id: 'WORKER', name: $t('additional_field.WORKER') },
                                            { id: 'CONTRAT', name: $t('additional_field.CONTRAT') }
                                        ]"
                                            :readonly="readonly"
                                            :rules="[mxRules.required]"
                                        />
                                    </v-col>
                                    <v-col cols="6">
                                        <w-label :label="$t('common.field')" required/>
                                        <w-enum-select
                                            v-model="additionalField.field"
                                            :items="fields"
                                            :readonly="readonly"
                                            :rules="[mxRules.required]"
                                        />
                                    </v-col>
                                    <v-col cols="12">
                                        <v-switch
                                            v-model="additionalField.required"
                                            :label="$t('common.required')"
                                            color="teal"
                                            :readonly="readonly"
                                        />
                                    </v-col>
                                </v-row>
                            </w-fieldset>
                            <br/>
                            <w-fieldset
                                v-if="additionalField.field === 'SELECT'"
                                :title="$t('common.data')"
                                :show-add-action="!readonly"
                                @add="additionalField.data.push({})"
                            >
                                <v-row dense v-if="additionalField.field === 'SELECT'">
                                    <template v-for="(d, index) in additionalField.data">
                                        <v-col cols="11" :key="`txt-${index}`">
                                            <w-label :label="$t('common.label')" required/>
                                            <w-text-field
                                                v-model="d.name"
                                                :readonly="readonly"
                                                :rules="[mxRules.required]"
                                            />
                                        </v-col>
                                        <v-col cols="1" :key="`btn-${index}`">
                                            <w-btn-icon
                                                color="red"
                                                icon="mdi-trash-can"
                                                classe="mt-6"
                                                :disabled="readonly"
                                                @press="additionalField.data.splice(index, 1)"
                                            />
                                        </v-col>
                                    </template>

                                </v-row>
                            </w-fieldset>
                        </v-container>
                    </v-form>
                </v-card-text>
            </v-card-text>
            <v-divider/>
            <v-card-actions>
                <v-spacer/>
                <w-btn-close @press="getBack"/>
                <w-btn-add
                    :show="addAction"
                    :loading="loading"
                    @press="save(false)"
                />
                <w-btn-update
                    :show="updateAction"
                    :loading="loading"
                    @press="update"
                />
                <w-btn-delete
                    :show="deleteAction"
                    :loading="loading"
                    @press="overlay = true"
                />
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>